import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { money } from '../treasury/treasuryUi';

const CHART_BODY_HEIGHT = 260;
const PAD = { top: 16, right: 18, bottom: 44, left: 64 };

const axisPointLabel = (index, monthAxis = []) => {
  const item = monthAxis[index];
  if (item?.month && item?.year) {
    const monthName = MONTH_LABELS[(Number(item.month) || 1) - 1] || item.short_label || item.label;
    const prev = monthAxis[index - 1];
    const showYear =
      index === 0 || Number(item.month) === 1 || (prev && Number(prev.year) !== Number(item.year));
    return {
      top: monthName,
      bottom: showYear ? String(item.year) : '',
      hover: item.label || `${monthName} ${item.year}`,
    };
  }
  const fallback = item?.label || item?.short_label || MONTH_LABELS[index] || String(index + 1);
  return { top: fallback, bottom: '', hover: fallback };
};

const formatAxis = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })}k`;
  return `${sign}$${Math.round(abs).toLocaleString('es-CL')}`;
};

const usePlotSize = () => {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const apply = () => {
      const next = {
        width: Math.max(1, Math.floor(node.clientWidth)),
        height: Math.max(1, Math.floor(node.clientHeight)),
      };
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [wrapRef, size];
};

const DualLineChart = ({ budgetSeries, actualSeries, ui, isDarkMode, monthAxis = [] }) => {
  const [hover, setHover] = useState(null);
  const [wrapRef, size] = usePlotSize();
  const width = size.width;
  const height = size.height;
  const plotW = Math.max(width - PAD.left - PAD.right, 1);
  const plotH = Math.max(height - PAD.top - PAD.bottom, 1);
  const points = useMemo(() => {
    const count = Math.max(budgetSeries.length, actualSeries.length, monthAxis.length, 1);
    return Array.from({ length: count }).map((_, index) => {
      const names = axisPointLabel(index, monthAxis);
      return {
        index,
        label: names.hover,
        topLabel: names.top,
        bottomLabel: names.bottom,
        budget: Number(budgetSeries[index] || 0),
        actual: Number(actualSeries[index] || 0),
      };
    });
  }, [budgetSeries, actualSeries, monthAxis]);
  const values = points.flatMap((point) => [point.budget, point.actual]);
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = Math.max(max - min, 1);
  const last = Math.max(points.length - 1, 1);
  const labelSize = points.length > 12 ? 9 : 11;
  const coords = points.map((point, index) => ({
    ...point,
    x: PAD.left + (plotW * index) / last,
    budgetY: PAD.top + ((max - point.budget) / span) * plotH,
    actualY: PAD.top + ((max - point.actual) / span) * plotH,
  }));
  const budgetLine = coords.map((point) => `${point.x},${point.budgetY}`).join(' ');
  const actualLine = coords.map((point) => `${point.x},${point.actualY}`).join(' ');
  const grid = isDarkMode ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.35)';
  const budgetColor = '#0d9488';
  const actualColor = '#0369a1';
  const ticks = 4;

  const onMove = (event) => {
    if (!coords.length || !width) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    let nearest = coords[0];
    let best = Math.abs(coords[0].x - x);
    coords.forEach((point) => {
      const dist = Math.abs(point.x - x);
      if (dist < best) {
        best = dist;
        nearest = point;
      }
    });
    setHover(nearest);
  };

  if (!width || !height) {
    return <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }} />;
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
      >
        {Array.from({ length: ticks + 1 }).map((_, index) => {
          const value = max - (span * index) / ticks;
          const y = PAD.top + (plotH * index) / ticks;
          return (
            <g key={`tick-${index}`}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} stroke={grid} strokeWidth="1" />
              <text x={PAD.left - 8} y={y + 3} textAnchor="end" fill={ui.muted} fontSize="11">
                {formatAxis(value)}
              </text>
            </g>
          );
        })}
        {coords.length ? (
          <>
            <polyline points={budgetLine} fill="none" stroke={budgetColor} strokeWidth="2.5" strokeLinejoin="round" />
            <polyline
              points={actualLine}
              fill="none"
              stroke={actualColor}
              strokeWidth="2.5"
              strokeDasharray="6 4"
              strokeLinejoin="round"
            />
          </>
        ) : null}
        {coords.map((point) => (
          <g key={`label-${point.index}`}>
            <text x={point.x} y={height - (point.bottomLabel ? 18 : 6)} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
              {point.topLabel}
            </text>
            {point.bottomLabel ? (
              <text x={point.x} y={height - 6} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
                {point.bottomLabel}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      {hover ? (
        <div
          style={{
            position: 'absolute',
            left: `${Math.min(82, Math.max(8, (hover.x / width) * 100))}%`,
            top: 8,
            transform: 'translateX(-50%)',
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: '8px 10px',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            pointerEvents: 'none',
            minWidth: 160,
            zIndex: 2,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>{hover.label}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: budgetColor, marginTop: 4 }}>
            Presupuesto {money(hover.budget)}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: actualColor }}>Real {money(hover.actual)}</div>
        </div>
      ) : null}
    </div>
  );
};

const GroupedBarChart = ({ budgetSeries, actualSeries, ui, isDarkMode, monthAxis = [] }) => {
  const [hover, setHover] = useState(null);
  const [wrapRef, size] = usePlotSize();
  const width = size.width;
  const height = size.height;
  const plotW = Math.max(width - PAD.left - PAD.right, 1);
  const plotH = Math.max(height - PAD.top - PAD.bottom, 1);
  const points = useMemo(() => {
    const count = Math.max(budgetSeries.length, actualSeries.length, monthAxis.length, 1);
    return Array.from({ length: count }).map((_, index) => {
      const names = axisPointLabel(index, monthAxis);
      return {
        index,
        label: names.hover,
        topLabel: names.top,
        bottomLabel: names.bottom,
        budget: Number(budgetSeries[index] || 0),
        actual: Number(actualSeries[index] || 0),
      };
    });
  }, [budgetSeries, actualSeries, monthAxis]);
  const labelSize = points.length > 12 ? 9 : 11;
  const values = points.flatMap((point) => [point.budget, point.actual, 0]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 1);
  const zeroY = PAD.top + ((max - 0) / span) * plotH;
  const slot = plotW / Math.max(points.length, 1);
  const barW = Math.max(Math.min(slot * 0.32, 18), 4);
  const grid = isDarkMode ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.35)';
  const budgetColor = '#0d9488';
  const actualColor = '#0369a1';
  const ticks = 4;
  const yFor = (value) => PAD.top + ((max - value) / span) * plotH;

  if (!width || !height) {
    return <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }} />;
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        {Array.from({ length: ticks + 1 }).map((_, index) => {
          const value = max - (span * index) / ticks;
          const y = PAD.top + (plotH * index) / ticks;
          return (
            <g key={`tick-${index}`}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} stroke={grid} strokeWidth="1" />
              <text x={PAD.left - 8} y={y + 3} textAnchor="end" fill={ui.muted} fontSize="11">
                {formatAxis(value)}
              </text>
            </g>
          );
        })}
        <line x1={PAD.left} x2={width - PAD.right} y1={zeroY} y2={zeroY} stroke={ui.border} strokeDasharray="4 4" />
        {points.map((point, index) => {
          const cx = PAD.left + slot * index + slot / 2;
          const budgetY = yFor(point.budget);
          const actualY = yFor(point.actual);
          const budgetH = Math.abs(zeroY - budgetY);
          const actualH = Math.abs(zeroY - actualY);
          return (
            <g
              key={point.index}
              onMouseEnter={() => setHover({ ...point, x: cx })}
              onMouseLeave={() => setHover(null)}
            >
              <rect
                x={cx - barW - 2}
                y={Math.min(zeroY, budgetY)}
                width={barW}
                height={budgetH}
                fill={budgetColor}
                rx="2"
              />
              <rect
                x={cx + 2}
                y={Math.min(zeroY, actualY)}
                width={barW}
                height={actualH}
                fill={actualColor}
                rx="2"
              />
              <text x={cx} y={height - (point.bottomLabel ? 18 : 6)} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
                {point.topLabel}
              </text>
              {point.bottomLabel ? (
                <text x={cx} y={height - 6} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
                  {point.bottomLabel}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {hover ? (
        <div
          style={{
            position: 'absolute',
            left: `${Math.min(82, Math.max(8, (hover.x / width) * 100))}%`,
            top: 8,
            transform: 'translateX(-50%)',
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: '8px 10px',
            pointerEvents: 'none',
            minWidth: 160,
            zIndex: 2,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>{hover.label}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: budgetColor, marginTop: 4 }}>
            Presupuesto {money(hover.budget)}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: actualColor }}>Real {money(hover.actual)}</div>
        </div>
      ) : null}
    </div>
  );
};

const ChartCard = ({ title, subtitle, value, ui, children }) => (
  <div
    style={{
      background: ui.panel,
      border: `1px solid ${ui.border}`,
      borderRadius: 10,
      minWidth: 0,
      minHeight: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '7px 10px',
        borderBottom: `1px solid ${ui.border}`,
        background: ui.header,
        flexShrink: 0,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text, lineHeight: 1.2 }}>{title}</h3>
        <p style={{ margin: '2px 0 0', fontSize: 11, color: ui.muted }}>{subtitle}</p>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: ui.text, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
    <div style={{ flex: 1, minHeight: CHART_BODY_HEIGHT, position: 'relative', padding: '8px 12px 10px' }}>
      {children}
    </div>
  </div>
);

const BudgetVsActualChart = ({
  monthlyBudget = [],
  monthlyActual = [],
  monthAxis = [],
  periodTitle = '',
  ui,
  isDarkMode = false,
  loading = false,
}) => {
  const cumulativeBudget = useMemo(() => {
    let running = 0;
    return (monthlyBudget || []).map((value) => {
      running += Number(value || 0);
      return running;
    });
  }, [monthlyBudget]);
  const cumulativeActual = useMemo(() => {
    let running = 0;
    return (monthlyActual || []).map((value) => {
      running += Number(value || 0);
      return running;
    });
  }, [monthlyActual]);
  const endBudget = cumulativeBudget[cumulativeBudget.length - 1] || 0;
  const endActual = cumulativeActual[cumulativeActual.length - 1] || 0;

  if (loading) {
    return <p style={{ margin: 0, padding: '24px 12px', fontSize: 13, color: ui.muted }}>Calculando…</p>;
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 12,
        alignItems: 'stretch',
      }}
    >
      <ChartCard title="Resultado acumulado" subtitle={periodTitle || 'Presupuesto vs real'} value={money(endBudget)} ui={ui}>
        <DualLineChart
          budgetSeries={cumulativeBudget}
          actualSeries={cumulativeActual}
          monthAxis={monthAxis}
          ui={ui}
          isDarkMode={isDarkMode}
        />
      </ChartCard>
      <ChartCard title="Resultado mensual" subtitle="Barras agrupadas" value={money(endBudget)} ui={ui}>
        <GroupedBarChart
          budgetSeries={monthlyBudget}
          actualSeries={monthlyActual}
          monthAxis={monthAxis}
          ui={ui}
          isDarkMode={isDarkMode}
        />
      </ChartCard>
    </div>
  );
};

export default BudgetVsActualChart;
